import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  IconButton,
  Slider,
  Avatar,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Storage as DatabaseIcon,
  PlayArrow as PlayIcon,
  ArrowForward as ArrowIcon,
  Verified as VerifiedIcon,
  ChevronLeft as PrevIcon,
  ChevronRight as NextIcon,
  School as SchoolIcon,
  Science as ScienceIcon,
  AccountBalance as InstitutionIcon,
  FormatQuote as QuoteIcon,
  Bolt as BoltIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { methodLabel } from '../utils/methodLabel';

const DEMO_SLUG = 'regresion-multiple-salarios';

const METHODS = [
  { label: 'Desigualdad y pobreza', category: 'Desigualdad y pobreza' },
  { label: 'Educación', category: 'Educación' },
  { label: 'Trabajo y empleo', category: 'Trabajo y empleo' },
  { label: 'Género', category: 'Género' },
  { label: 'Salud y bienestar', category: 'Salud y bienestar' },
  { label: 'Economía y empresas', category: 'Economía y empresas' },
];

const AUDIENCES = [
  { icon: <SchoolIcon />, title: 'Estudiantes', text: 'Aprende replicando proyectos reales: ETL, EDA y modelos paso a paso, ejecutables en vivo.' },
  { icon: <ScienceIcon />, title: 'Investigadores', text: 'Publica tu estudio con datos y código abiertos, y obtén una certificación verificable.' },
  { icon: <InstitutionIcon />, title: 'Instituciones', text: 'Valida proyectos y emite certificados on-chain con el sello de tu universidad.' },
];

const TESTIMONIALS = [
  { quote: 'Por fin puedo replicar un paper sin pelearme con el entorno: abro el notebook y corre.', name: 'M. García', role: 'Doctoranda en Economía' },
  { quote: 'Subimos los proyectos del curso y los alumnos los ejecutan en vivo. Cambió la clase.', name: 'C. López', role: 'Profesor de Ciencia de Datos' },
  { quote: 'La certificación on-chain nos da trazabilidad real de lo que avalamos como institución.', name: 'Dirección de Calidad', role: 'Universidad' },
];

// Universidades participantes (MVP: wordmarks; logos reales más adelante).
const UNIVERSIDADES = [
  'UNAM', 'IPN', 'Tec de Monterrey', 'UACM', 'UAM', 'UDG',
  'BUAP', 'El Colegio de México', 'ITAM', 'CIDE',
];

// Banda horizontal "La propuesta" integrada en la landing (scroll lateral).
const PROPUESTA_SLIDES = [
  { k: 'El problema', t: 'La ciencia no se puede repetir', d: '70% de los investigadores no logra reproducir el trabajo de otro; de 864 mil notebooks, solo 4% reproduce su propio resultado.' },
  { k: 'Lo que existe', t: 'Ninguna plataforma cierra el círculo', d: 'Kaggle ejecuta pero no certifica; GitHub versiona pero no corre; en Dataverse, el 74% del código no se ejecuta.' },
  { k: 'La propuesta', t: 'Reproducible, ejecutable, verificado y reconocido', d: 'Convertimos cada investigación en un activo que corre, se sella y se reconoce — por el mercado y por la evaluación.' },
  { k: 'Estándar', t: 'ETL → EDA → Modelo → Despliegue', d: 'Un flujo fijo en notebooks legibles, con decisiones explícitas y reutilizables.' },
  { k: 'En vivo', t: 'Reproducir es un clic', d: 'JupyterLab en el navegador, con entorno listo: sin instalar nada, sin setup de horas.' },
  { k: 'Sociedad', t: 'El producto no muere con la investigación', d: 'Cada proyecto expone un modelo interactivo: la gente explora “qué pasaría si” se tomaran en cuenta los resultados.' },
  { k: 'Certificación', t: 'Base, código o proyecto — con sello', d: 'Verificable e inalterable. En el SNII, una base o un software equivalen a 1 artículo; una IP otorgada, a 4.' },
  { k: 'Modelo', t: 'Una nueva economía de la ciencia', d: 'Suscripción institucional + venta de activos certificados. En la era de la IA, el dato trazable es el insumo escaso.' },
];

const HEADLINES = [
  {
    pre: 'Replica, aprende y ',
    em: 'certifica',
    post: ' ciencia de datos abierta',
    sub: 'Datos legitimados, código abierto y notebooks que corren en vivo. Comparte, reutiliza y certifica tu trabajo con respaldo en blockchain.',
  },
  {
    pre: 'Ciencia de datos que ',
    em: 'cualquiera puede correr',
    post: '',
    sub: 'Abre cualquier proyecto, ejecútalo en vivo en tu propio JupyterLab y verifica cada resultado paso a paso.',
  },
  {
    pre: 'Tu investigación, ',
    em: 'certificada on-chain',
    post: ' por tu institución',
    sub: 'Convierte la replicabilidad en reputación verificable: las universidades validan y sellan cada proyecto en blockchain.',
  },
];

// Etiqueta del modelo/técnica usado, derivada del proyecto.
const Home = () => {
  const navigate = useNavigate();
  const [projectCount, setProjectCount] = useState(null);
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [showcaseIdx, setShowcaseIdx] = useState(0);
  const [headlineIdx, setHeadlineIdx] = useState(0);
  const [certBySlug, setCertBySlug] = useState({});

  // Playground en vivo
  const [pgInfo, setPgInfo] = useState(null);
  const [pgValues, setPgValues] = useState({});
  const [pgResult, setPgResult] = useState(null);
  const [pgLoading, setPgLoading] = useState(false);

  // Banda horizontal de la propuesta (scroll lateral con flechas)
  const propuestaRef = useRef(null);
  const scrollPropuesta = (dir) => {
    const el = propuestaRef.current;
    if (el) el.scrollBy({ left: dir * Math.round(el.clientWidth * 0.85), behavior: 'smooth' });
  };

  useEffect(() => {
    fetch('/api/projects')
      .then((r) => r.json())
      .then((data) => {
        const list = data.projects || [];
        setProjectCount(list.length);
        setFeaturedProjects(list.slice(0, 4));
      })
      .catch(() => {});
  }, []);

  // Certificados reales para el badge del hero.
  useEffect(() => {
    fetch('/api/certificates/search')
      .then((r) => r.json())
      .then((data) => {
        const map = {};
        (data.results || []).forEach((c) => {
          if (c.project && !map[c.project]) map[c.project] = c;
        });
        setCertBySlug(map);
      })
      .catch(() => {});
  }, []);

  // Modelo demo para el playground.
  useEffect(() => {
    fetch(`/api/projects/${DEMO_SLUG}/model/info`)
      .then((r) => r.json())
      .then((info) => {
        if (!info || !info.inputs) return;
        setPgInfo(info);
        const defaults = {};
        info.inputs.forEach((i) => { defaults[i.name] = i.example != null ? i.example : i.min || 0; });
        setPgValues(defaults);
      })
      .catch(() => {});
  }, []);

  // Rotación del showcase (proyecto) cada 10s.
  const showcaseList = featuredProjects.length
    ? featuredProjects
    : [{ title: 'Salario mínimo y empleo', category: 'Econometría', slug: null }];
  useEffect(() => {
    if (showcaseList.length <= 1) return undefined;
    const timer = setInterval(() => setShowcaseIdx((p) => (p + 1) % showcaseList.length), 10000);
    return () => clearInterval(timer);
  }, [showcaseList.length]);

  // Rotación del titular con SU PROPIO ritmo (8s), independiente de las flechas.
  useEffect(() => {
    const timer = setInterval(() => setHeadlineIdx((p) => (p + 1) % HEADLINES.length), 8000);
    return () => clearInterval(timer);
  }, []);

  const safeIdx = showcaseList.length ? showcaseIdx % showcaseList.length : 0;
  const showcase = showcaseList[safeIdx];
  const goPrev = () => setShowcaseIdx((p) => (p - 1 + showcaseList.length) % showcaseList.length);
  const goNext = () => setShowcaseIdx((p) => (p + 1) % showcaseList.length);
  // Métricas REALES del proyecto en pantalla (de p.stats).
  const st = showcase.stats || {};
  const fmtNum = (n) => (Number(n) >= 1000 ? `${(Number(n) / 1000).toFixed(1)}k` : `${Number(n) || 0}`);
  const cert = showcase.slug ? certBySlug[showcase.slug] : null;
  const shortHash = cert?.hash ? `${cert.hash.slice(0, 10)}…${cert.hash.slice(-4)}` : null;
  const headline = HEADLINES[headlineIdx % HEADLINES.length];

  // Inputs visibles del playground (hasta 4, sin el término cuadrático).
  const pgFields = (pgInfo?.inputs || []).filter((i) => !/2$|cuadrad/i.test(i.name)).slice(0, 4);

  const runPredict = useCallback(() => {
    if (!pgInfo) return;
    setPgLoading(true);
    setPgResult(null);
    fetch(`/api/projects/${DEMO_SLUG}/predict`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(pgValues),
    })
      .then((r) => r.json())
      .then((data) => {
        setPgLoading(false);
        setPgResult(typeof data.prediction === 'number' ? data.prediction : (data.prediction ?? null));
      })
      .catch(() => setPgLoading(false));
  }, [pgInfo, pgValues]);

  const pgOutLabel = pgInfo?.output_label || 'Predicción';
  const pgIsLog = /log/i.test(pgOutLabel);

  return (
    <Box sx={{ bgcolor: 'background.default' }}>
      {/* Hero - vibrante (collage) */}
      <Box
        component="section"
        sx={{
          pt: { xs: 5, md: 7 },
          pb: { xs: 6, md: 9 },
          position: 'relative',
          overflow: 'hidden',
          background: (t) =>
            t.palette.mode === 'dark'
              ? 'radial-gradient(720px 340px at 88% -8%, rgba(45,212,167,0.16), transparent 60%)'
              : 'radial-gradient(780px 360px at 88% -8%, rgba(14,124,102,0.13), transparent 60%)',
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={{ xs: 5, md: 4 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Chip
                label={`+${projectCount ?? 15} estudios reproducibles`}
                size="small"
                sx={{ mb: 2.5, bgcolor: 'rgba(14,124,102,0.10)', color: 'primary.dark', fontWeight: 600 }}
              />
              <Box sx={{ mb: 3, minHeight: { md: 215 } }}>
                <motion.div
                  key={`hl-${headlineIdx % HEADLINES.length}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45 }}
                >
                  <Typography
                    variant="h1"
                    sx={{ fontWeight: 600, letterSpacing: '-0.035em', lineHeight: 1.04, fontSize: { xs: '2.4rem', md: '3.3rem' }, mb: 2 }}
                  >
                    {headline.pre}
                    <Box component="span" sx={{ color: 'primary.main' }}>{headline.em}</Box>
                    {headline.post}
                  </Typography>
                  <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, lineHeight: 1.6, maxWidth: '46ch' }}>
                    {headline.sub}
                  </Typography>
                </motion.div>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3.5 }}>
                <Button
                  variant="contained"
                  size="large"
                  endIcon={<ArrowIcon />}
                  onClick={() => navigate('/proyectos')}
                  sx={{ px: 3.5, py: 1.25, fontWeight: 600, boxShadow: '0 10px 24px rgba(14,124,102,0.28)' }}
                >
                  Explorar proyectos
                </Button>
                <Button variant="outlined" size="large" onClick={() => navigate('/certificados')} sx={{ px: 3.5, py: 1.25 }}>
                  Validar certificado
                </Button>
              </Stack>
              <Stack direction="row" spacing={3.5}>
                {[{ v: '100%', l: 'replicable' }, { v: 'On-chain', l: 'verificado' }, { v: 'En vivo', l: 'Jupyter' }].map((s) => (
                  <Box key={s.l}>
                    <Typography sx={{ fontWeight: 600, fontSize: '1.15rem', color: 'text.primary' }}>{s.v}</Typography>
                    <Typography variant="caption" color="text.secondary">{s.l}</Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box>
                {/* Tarjeta principal rotativa */}
                <Card
                  sx={{
                    width: '100%',
                    boxShadow: '0 18px 46px rgba(8,60,48,0.16)',
                    cursor: showcase.slug ? 'pointer' : 'default',
                  }}
                  onClick={() => showcase.slug && navigate(`/proyecto/${showcase.slug}`)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.25, borderBottom: '0.5px solid', borderColor: 'divider' }}>
                    <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
                      <Chip label={showcase.category || 'Proyecto'} size="small" sx={{ bgcolor: 'rgba(14,124,102,0.10)', color: 'primary.dark', fontWeight: 500 }} />
                      {methodLabel(showcase) && (
                        <Chip label={methodLabel(showcase)} size="small" variant="outlined" sx={{ borderColor: 'divider', fontWeight: 500 }} />
                      )}
                    </Stack>
                    <VerifiedIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  </Box>
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.25 }} noWrap>{showcase.title}</Typography>
                    {/* Gráfico REAL del notebook del proyecto */}
                    <Box sx={{ height: 184, borderRadius: 2, overflow: 'hidden', border: '0.5px solid', borderColor: 'divider', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {showcase.slug ? (
                        <Box
                          component="img"
                          src={`/static/proyectos/${showcase.slug}/preview.png`}
                          alt={`Resultado del análisis de ${showcase.title}`}
                          sx={{ width: '100%', height: '100%', objectFit: 'contain', p: 0.5 }}
                          onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">Vista del análisis</Typography>
                      )}
                    </Box>
                    <Stack direction="row" spacing={2} sx={{ mt: 1.25 }} color="text.secondary">
                      <Typography variant="caption" color="text.secondary"><b style={{ fontWeight: 600 }}>{fmtNum(st.views)}</b> vistas</Typography>
                      <Typography variant="caption" color="text.secondary"><b style={{ fontWeight: 600 }}>{fmtNum(st.likes)}</b> me gusta</Typography>
                      <Typography variant="caption" color="text.secondary"><b style={{ fontWeight: 600 }}>{fmtNum(st.downloads)}</b> descargas</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} useFlexGap flexWrap="wrap">
                      {[{ l: 'ETL', nb: 'etl' }, { l: 'EDA', nb: 'eda' }, { l: 'Modelo', nb: 'model' }].map((t) => (
                        <Chip
                          key={t.l}
                          label={t.l}
                          size="small"
                          variant="outlined"
                          clickable={Boolean(showcase.slug)}
                          onClick={(e) => { e.stopPropagation(); if (showcase.slug) navigate(`/proyecto/${showcase.slug}?nb=${t.nb}`); }}
                          sx={{ borderColor: 'divider', '&:hover': showcase.slug ? { borderColor: 'primary.main', color: 'primary.main' } : {} }}
                        />
                      ))}
                      <Chip
                        icon={<PlayIcon sx={{ fontSize: 16 }} />}
                        label="En vivo"
                        size="small"
                        onClick={(e) => { e.stopPropagation(); if (showcase.slug) navigate(`/proyecto/${showcase.slug}`); }}
                        sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', cursor: 'pointer', '& .MuiChip-icon': { color: 'primary.contrastText' } }}
                      />
                    </Stack>
                    {showcaseList.length > 1 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                        <IconButton size="small" aria-label="Anterior" onClick={(e) => { e.stopPropagation(); goPrev(); }} sx={{ border: '0.5px solid', borderColor: 'divider' }}>
                          <PrevIcon fontSize="small" />
                        </IconButton>
                        <Stack direction="row" spacing={0.75}>
                          {showcaseList.map((_, i) => (
                            <Box key={i} role="button" aria-label={`Proyecto ${i + 1}`} onClick={(e) => { e.stopPropagation(); setShowcaseIdx(i); }} sx={{ width: i === safeIdx ? 20 : 7, height: 7, borderRadius: 999, cursor: 'pointer', bgcolor: i === safeIdx ? 'primary.main' : 'divider', transition: 'all 0.25s ease' }} />
                          ))}
                        </Stack>
                        <IconButton size="small" aria-label="Siguiente" onClick={(e) => { e.stopPropagation(); goNext(); }} sx={{ border: '0.5px solid', borderColor: 'divider' }}>
                          <NextIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </CardContent>
                </Card>

                {/* Badge certificado real (cambia con el proyecto) */}
                <Card
                  sx={{ width: '100%', mt: 1.75, boxShadow: '0 16px 40px rgba(8,60,48,0.20)', cursor: cert ? 'pointer' : 'default' }}
                  onClick={() => cert && navigate(`/certificados?hash=${cert.hash}`)}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.75, '&:last-child': { pb: 1.75 } }}>
                    <Box sx={{ width: 38, height: 38, borderRadius: 2, bgcolor: cert ? 'primary.main' : 'action.disabledBackground', color: 'primary.contrastText', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <VerifiedIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <motion.div key={`cert-${safeIdx}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }} style={{ minWidth: 0 }}>
                      {cert ? (
                        <>
                          <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>Certificado · {cert.institution}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                            bloque #{cert.block != null ? Number(cert.block).toLocaleString() : '—'} · {shortHash || 'on-chain'}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>Aún sin certificado</Typography>
                          <Typography variant="caption" color="text.secondary">Este proyecto no está certificado</Typography>
                        </>
                      )}
                    </motion.div>
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Cinta de universidades participantes (marquee) */}
      <Box component="section" sx={{ pt: { xs: 4, md: 6 }, pb: { xs: 2, md: 3 } }}>
        <Typography variant="overline" align="center" sx={{ display: 'block', color: 'text.secondary', letterSpacing: '0.12em', mb: 2.5 }}>
          Universidades participantes
        </Typography>
        <Box
          sx={{
            overflow: 'hidden',
            position: 'relative',
            WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)',
            maskImage: 'linear-gradient(90deg, transparent, #000 8%, #000 92%, transparent)',
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: { xs: 4, md: 6 },
              whiteSpace: 'nowrap',
              '@keyframes scrollX': { from: { transform: 'translateX(0)' }, to: { transform: 'translateX(-50%)' } },
              animation: 'scrollX 32s linear infinite',
              '&:hover': { animationPlayState: 'paused' },
            }}
          >
            {[...UNIVERSIDADES, ...UNIVERSIDADES].map((u, i) => (
              <Box key={i} sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                <InstitutionIcon sx={{ fontSize: 22, color: 'primary.main', opacity: 0.85 }} />
                <Typography sx={{ fontWeight: 600, fontSize: '1.05rem', letterSpacing: '-0.01em' }}>{u}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      {/* La propuesta — banda horizontal integrada en la landing */}
      <Box component="section" sx={{ pt: { xs: 3, md: 4 }, pb: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Stack direction="row" alignItems="flex-end" justifyContent="space-between" sx={{ mb: 2.5 }}>
            <Box>
              <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: '0.08em' }}>La propuesta</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>Por qué SciDataHub</Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ display: { xs: 'none', sm: 'flex' } }}>
              <IconButton aria-label="Anterior" onClick={() => scrollPropuesta(-1)} sx={{ border: '1px solid', borderColor: 'divider', fontSize: '1.2rem' }}>‹</IconButton>
              <IconButton aria-label="Siguiente" onClick={() => scrollPropuesta(1)} sx={{ border: '1px solid', borderColor: 'divider', fontSize: '1.2rem' }}>›</IconButton>
            </Stack>
          </Stack>
        </Container>
        <Box
          ref={propuestaRef}
          sx={{
            display: 'flex', gap: 2, overflowX: 'auto', scrollSnapType: 'x mandatory',
            px: { xs: 2.5, md: 'max(24px, calc((100vw - 1200px) / 2))' }, pb: 1.5,
            scrollbarWidth: 'thin',
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-thumb': { background: 'rgba(14,124,102,.3)', borderRadius: 8 },
          }}
        >
          {PROPUESTA_SLIDES.map((s, i) => (
            <Card key={i} variant="outlined" sx={{ flex: '0 0 auto', width: { xs: '82%', sm: 320, md: 350 }, scrollSnapAlign: 'start', borderRadius: 3, bgcolor: i % 2 ? 'rgba(14,124,102,0.04)' : 'background.paper' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography sx={{ fontFamily: 'Georgia, serif', color: 'primary.main', fontWeight: 700, fontSize: '1.4rem', mb: 0.5 }}>{String(i + 1).padStart(2, '0')}</Typography>
                <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: '0.08em' }}>{s.k}</Typography>
                <Typography sx={{ fontWeight: 700, fontSize: '1.12rem', lineHeight: 1.22, mt: 0.5, mb: 1 }}>{s.t}</Typography>
                <Typography variant="body2" color="text.secondary">{s.d}</Typography>
              </CardContent>
            </Card>
          ))}
          <Card variant="outlined" sx={{ flex: '0 0 auto', width: { xs: '82%', sm: 320, md: 350 }, scrollSnapAlign: 'start', borderRadius: 3, bgcolor: 'primary.main', color: '#fff', border: 'none' }}>
            <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center' }}>
              <Typography sx={{ fontWeight: 700, fontSize: '1.2rem', lineHeight: 1.2, mb: 1 }}>Ve la propuesta completa</Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,.85)', mb: 2 }}>Presentación interactiva de 24 diapositivas.</Typography>
              <Button component="a" href="/presentacion.html" target="_blank" rel="noopener noreferrer" variant="contained" sx={{ bgcolor: '#fff', color: 'primary.main', fontWeight: 700, alignSelf: 'flex-start', '&:hover': { bgcolor: 'rgba(255,255,255,.9)' } }}>Abrir presentación</Button>
            </CardContent>
          </Card>
        </Box>
        <Container maxWidth="lg">
          <Typography variant="caption" sx={{ color: 'text.secondary', display: { xs: 'block', sm: 'none' }, mt: 1 }}>Desliza para ver más →</Typography>
        </Container>
      </Box>

      {/* Banda institucional */}
      <Box component="section" sx={{ py: { xs: 3, md: 3.5 }, bgcolor: '#0b3b30' }}>
        <Container maxWidth="lg">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '1.05rem' }}>Convierte tu institución en certificadora</Typography>
              <Typography variant="body2" sx={{ color: '#bfe5d8' }}>Las universidades validan proyectos y emiten el sello verificable en blockchain.</Typography>
            </Box>
            <Button variant="contained" onClick={() => navigate('/certificados')} sx={{ bgcolor: '#5fd9b6', color: '#06241d', fontWeight: 600, whiteSpace: 'nowrap', '&:hover': { bgcolor: '#3bbf99' } }}>
              Conocer más
            </Button>
          </Stack>
        </Container>
      </Box>

      {/* El estándar + traducción como servicio */}
      <Box component="section" sx={{ py: { xs: 5, md: 7 }, bgcolor: 'background.paper', borderTop: '0.5px solid', borderBottom: '0.5px solid', borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: '0.08em' }}>Un estándar común</Typography>
          <Typography variant="h4" sx={{ fontWeight: 600, letterSpacing: '-0.02em', mt: 1, mb: 1.5 }}>
            ETL → EDA → Modelo, en Python
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 760, mb: 3 }}>
            Toda investigación en SciDataHub sigue el <b>estándar del mercado</b>: limpieza de datos (ETL),
            análisis exploratorio (EDA) y modelo, en <b>notebooks de Python</b> legibles, ejecutables y
            reutilizables. Así cualquiera puede seguir, verificar y mejorar el trabajo.
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 760, mb: 3 }}>
            La idea es <b>capitalizar lo que ya se hace</b>: proyectos que salen de diplomados, seminarios de
            tesis, capítulos y artículos —donde el trabajo ya fue validado—. Cada proyecto indica su origen
            (seminario, tesis, capítulo, artículo) y es <b>buscable</b> por ese criterio.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { n: '01', t: 'ETL', d: 'Datos crudos → base limpia y documentada.' },
              { n: '02', t: 'EDA', d: 'Distribuciones, correlaciones y hallazgos con gráficas.' },
              { n: '03', t: 'Modelo', d: 'Modelo interpretable + simulador interactivo en vivo.' },
            ].map((s) => (
              <Grid item xs={12} sm={4} key={s.t}>
                <Box sx={{ height: '100%', p: 2.5, borderRadius: 3, border: '0.5px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
                  <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 700 }}>{s.n}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>{s.t}</Typography>
                  <Typography variant="body2" color="text.secondary">{s.d}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3, bgcolor: 'rgba(14,124,102,0.06)', border: '1px solid', borderColor: 'primary.light' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
              <Box>
                <Typography sx={{ fontWeight: 700 }}>¿Tu proyecto está en R, Stata, SPSS o Excel?</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 620 }}>
                  No es obstáculo. Súbelo como esté y <b>nosotros lo llevamos al estándar</b> (Python: ETL, EDA y modelo),
                  conservando la lógica y la <b>certificación verificable</b>. Tú no tienes que reescribir nada.
                </Typography>
              </Box>
              <Button variant="contained" onClick={() => navigate('/subir-proyecto')} sx={{ whiteSpace: 'nowrap' }}>
                Subir un proyecto
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Explora por método */}
      <Box component="section" sx={{ py: { xs: 5, md: 7 } }}>
        <Container maxWidth="lg">
          <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: '0.08em' }}>Explora por área</Typography>
          <Typography variant="h4" sx={{ fontWeight: 600, letterSpacing: '-0.02em', mt: 1, mb: 3 }}>Encuentra proyectos por área de investigación</Typography>
          <Stack direction="row" spacing={1.25} useFlexGap flexWrap="wrap">
            {METHODS.map((m) => (
              <Chip
                key={m.label}
                label={m.label}
                onClick={() => navigate(`/proyectos?category=${encodeURIComponent(m.category)}`)}
                sx={{ px: 1, py: 2.25, fontSize: '0.9rem', cursor: 'pointer', bgcolor: 'background.paper', border: '0.5px solid', borderColor: 'divider', '&:hover': { borderColor: 'primary.main', color: 'primary.main' } }}
              />
            ))}
          </Stack>
        </Container>
      </Box>

      {/* Playground en vivo */}
      {pgFields.length > 0 && (
        <Box component="section" sx={{ py: { xs: 5, md: 7 }, bgcolor: 'background.paper', borderTop: '0.5px solid', borderBottom: '0.5px solid', borderColor: 'divider' }}>
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={5}>
                <Chip icon={<BoltIcon sx={{ fontSize: 16 }} />} label="Sin registrarte" size="small" sx={{ mb: 2, bgcolor: 'rgba(14,124,102,0.10)', color: 'primary.dark', fontWeight: 600, '& .MuiChip-icon': { color: 'primary.dark' } }} />
                <Typography variant="h4" sx={{ fontWeight: 600, letterSpacing: '-0.02em', mb: 1.5 }}>Pruébalo en vivo</Typography>
                <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6, mb: 2 }}>
                  Mueve los valores y obtén una predicción de un modelo real entrenado en la plataforma
                  ({pgInfo?.title || 'Determinantes del salario'}). Así se siente ejecutar cualquier proyecto aquí.
                </Typography>
                <Button variant="text" endIcon={<ArrowIcon />} onClick={() => navigate(`/proyecto/${DEMO_SLUG}`)}>Ver el proyecto completo</Button>
              </Grid>
              <Grid item xs={12} md={7}>
                <Card variant="outlined" sx={{ boxShadow: '0 12px 32px rgba(8,60,48,0.10)' }}>
                  <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                    <Grid container spacing={2.5}>
                      {pgFields.map((f) => {
                        const min = Number(f.min ?? 0);
                        const max = Number(f.max ?? 100);
                        const val = Number(pgValues[f.name] ?? min);
                        const binary = min === 0 && max === 1;
                        return (
                          <Grid item xs={12} sm={6} key={f.name}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">{f.label || f.name}</Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>{val}</Typography>
                            </Box>
                            <Slider
                              value={val}
                              min={min}
                              max={max}
                              step={binary ? 1 : (max - min > 50 ? 1 : 0.5)}
                              marks={binary}
                              onChange={(e, v) => setPgValues((prev) => ({ ...prev, [f.name]: v }))}
                              size="small"
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
                      <Button variant="contained" startIcon={pgLoading ? <CircularProgress size={16} color="inherit" /> : <PlayIcon />} onClick={runPredict} disabled={pgLoading} sx={{ fontWeight: 600 }}>
                        {pgLoading ? 'Calculando…' : 'Predecir'}
                      </Button>
                      {pgResult != null && (
                        <motion.div initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">{pgOutLabel}</Typography>
                            <Typography sx={{ fontWeight: 600, fontSize: '1.25rem', color: 'primary.main', lineHeight: 1.1 }}>
                              {Number(pgResult).toFixed(3)}
                              {pgIsLog && (
                                <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                                  ≈ {Math.round(Math.exp(Number(pgResult))).toLocaleString()}
                                </Typography>
                              )}
                            </Typography>
                          </Box>
                        </motion.div>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Container>
        </Box>
      )}

      {/* Cómo funciona */}
      <Box component="section" sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: '0.08em' }}>Cómo funciona</Typography>
          <Typography variant="h4" sx={{ fontWeight: 600, letterSpacing: '-0.02em', mb: 5, mt: 1 }}>Replica un proyecto en tres pasos</Typography>
          <Grid container spacing={3}>
            {[
              { n: '01', icon: <DatabaseIcon />, title: 'Explora un proyecto', text: 'Elige entre datos legitimados y replicables, con su contexto y objetivo.' },
              { n: '02', icon: <PlayIcon />, title: 'Ejecuta el código', text: 'Abre el ETL, EDA y los modelos en tu workspace Jupyter por usuario.' },
              { n: '03', icon: <VerifiedIcon />, title: 'Certifica el resultado', text: 'Obtén una certificación verificable en blockchain de tu trabajo.' },
            ].map((s) => (
              <Grid item xs={12} md={4} key={s.n}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
                      <Box sx={{ color: 'primary.main', '& svg': { fontSize: 26 } }}>{s.icon}</Box>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>{s.n}</Typography>
                    </Stack>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>{s.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{s.text}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Proyectos destacados */}
      {featuredProjects.length > 0 && (
        <Box component="section" sx={{ py: { xs: 6, md: 8 } }}>
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: 5, flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: '0.08em' }}>Proyectos</Typography>
                <Typography variant="h4" sx={{ fontWeight: 600, letterSpacing: '-0.02em', mt: 1 }}>Proyectos destacados</Typography>
              </Box>
              <Button variant="text" endIcon={<ArrowIcon />} onClick={() => navigate('/proyectos')}>Ver todos</Button>
            </Box>
            <Grid container spacing={3}>
              {featuredProjects.map((p, i) => (
                <Grid item xs={12} sm={6} md={3} key={p.slug || i}>
                  <Card
                    variant="outlined"
                    sx={{ height: '100%', cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
                    onClick={() => navigate(`/proyecto/${p.slug}`)}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Stack direction="row" spacing={0.75} sx={{ mb: 1.5 }} useFlexGap flexWrap="wrap">
                        <Chip label={p.category || 'Proyecto'} size="small" variant="outlined" color="primary" />
                        {methodLabel(p) && (
                          <Chip label={methodLabel(p)} size="small" sx={{ bgcolor: 'rgba(14,124,102,0.10)', color: 'primary.dark', fontWeight: 500 }} />
                        )}
                      </Stack>
                      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }} noWrap>{p.title}</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.6 }}>
                        {p.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
      )}

      {/* Para quién */}
      <Box component="section" sx={{ py: { xs: 6, md: 8 }, bgcolor: 'background.paper', borderTop: '0.5px solid', borderBottom: '0.5px solid', borderColor: 'divider' }}>
        <Container maxWidth="lg">
          <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: '0.08em' }}>Para quién</Typography>
          <Typography variant="h4" sx={{ fontWeight: 600, letterSpacing: '-0.02em', mb: 5, mt: 1 }}>Una plataforma, tres comunidades</Typography>
          <Grid container spacing={3}>
            {AUDIENCES.map((a) => (
              <Grid item xs={12} md={4} key={a.title}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: 'rgba(14,124,102,0.10)', color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2, '& svg': { fontSize: 24 } }}>{a.icon}</Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>{a.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{a.text}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testimonios */}
      <Box component="section" sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="lg">
          <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: '0.08em' }}>Qué dicen</Typography>
          <Typography variant="h4" sx={{ fontWeight: 600, letterSpacing: '-0.02em', mb: 5, mt: 1 }}>Confianza de la comunidad</Typography>
          <Grid container spacing={3}>
            {TESTIMONIALS.map((t, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 3 }}>
                    <QuoteIcon sx={{ color: 'primary.main', opacity: 0.5, mb: 1 }} />
                    <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 2.5 }}>“{t.quote}”</Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontSize: 14, fontWeight: 600 }}>{t.name[0]}</Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{t.role}</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Certificación on-chain + CTA final */}
      <Box component="section" sx={{ py: { xs: 8, md: 11 }, textAlign: 'center', position: 'relative', overflow: 'hidden',
        background: (t) => t.palette.mode === 'dark'
          ? 'radial-gradient(700px 300px at 50% 120%, rgba(45,212,167,0.14), transparent 60%)'
          : 'radial-gradient(700px 300px at 50% 120%, rgba(14,124,102,0.10), transparent 60%)' }}>
        <Container maxWidth="sm">
          <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 600, letterSpacing: '0.08em' }}>Certificación on-chain</Typography>
          <Typography variant="h3" sx={{ fontWeight: 600, letterSpacing: '-0.025em', mt: 1, mb: 2 }}>Empieza a replicar hoy</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
            Explora los proyectos, ejecuta tu primer notebook en minutos y verifica cualquier certificado de forma pública e independiente.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
            <Button variant="contained" size="large" endIcon={<ArrowIcon />} onClick={() => navigate('/proyectos')} sx={{ px: 4, py: 1.25, fontWeight: 600 }}>Explorar proyectos</Button>
            <Button variant="outlined" size="large" onClick={() => navigate('/certificados')} sx={{ px: 4, py: 1.25 }}>Validar certificado</Button>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
